import type { SiteFAQ, SiteSettings } from "@/types";
import { DEFAULT_RESTRICTED_STATES } from "@/lib/compliance";

// Differentiators — verbatim from brand_dna.md §"Differentiators". The 4 SVG-icon cards on the homepage.
export interface Differentiator {
  icon: "beaker" | "physician" | "document" | "target";
  title: string;
  body: string;
}

export const differentiators: Differentiator[] = [
  {
    icon: "beaker",
    title: "Rooted in Science",
    body: "Every formula is based on science, not folklore — and tested to a 99.5% accuracy rating by CBD Informative.",
  },
  {
    icon: "physician",
    title: "Physician Formulated",
    body: "Formulated by Dr. Elaine Burns, drawing on over 15 years and counting of clinical experience.",
  },
  {
    icon: "document",
    title: "Proprietary Guidelines",
    body: "Simple, easy-to-read labeling and detailed product reference guides for every formula.",
  },
  {
    icon: "target",
    title: "Condition Specific",
    body: "Formulations designed for fast, reliable results — pairing CBD with botanicals chosen to target specific conditions.",
  },
];

// Trust signals — the credential hierarchy. Lead with these (brand_dna.md §"Trust signal hierarchy").
export const trustSignals: string[] = [
  "AACM-Certified Physician",
  "99.5% Lab Accuracy",
  "15+ Years Clinical Experience",
  "Arizona-Licensed Naturopathic Physician",
];

export const siteFaqs: SiteFAQ[] = [
  {
    id: "f1",
    category: "Products",
    question: "Are your products physician-formulated?",
    answer:
      "Yes. Every DrBurns' ReLeaf formula is developed by Dr. Elaine Burns, an Arizona-licensed Naturopathic Physician and AACM-Certified cannabinoid medicine physician.",
    order: 1,
  },
  {
    id: "f2",
    category: "Products",
    question: "Will CBD make me feel high?",
    answer:
      "No. Our formulas are CBD-based with zero THC and are not intended to be intoxicating.",
    order: 2,
  },
  {
    id: "f3",
    category: "Products",
    question: "How are your products tested?",
    answer:
      "Every batch is third-party tested for potency and purity. The product line has achieved a 99.5% accuracy rating by CBD Informative.",
    order: 3,
  },
  {
    id: "f4",
    category: "Shipping",
    question: "How fast do you ship?",
    answer:
      "Orders placed before 2pm AZ time ship the same business day. Most orders arrive within 3-5 business days.",
    order: 4,
  },
  {
    id: "f5",
    category: "Shipping",
    question: "Do you offer free shipping?",
    answer: "Orders ship at a $6.95 flat rate anywhere in the US.",
    order: 5,
  },
  // DRB-38: the Subscribe & Save FAQ is removed with the feature. Leaving it would keep
  // answering a question about something the site no longer offers.
  {
    id: "f7",
    category: "Orders",
    question: "How do I place an order right now?",
    answer:
      "You can browse and order online. Add items to your cart and proceed to checkout. For any assistance, email us at orders@drburnsreleaf.com.",
    order: 7,
  },
  {
    id: "f8",
    category: "CBD 101",
    question: "What's the difference between full-spectrum and isolate?",
    answer:
      "Full-spectrum CBD retains the plant's natural range of cannabinoids and terpenes for an entourage effect, while isolate is pure CBD only. Most of our drops are full-spectrum.",
    order: 8,
  },
];

/**
 * PRE-HYDRATION FALLBACK ONLY — not the source of truth.
 *
 * The live values come from Firestore `siteSettings/global` via `useSiteSettings()`, which is
 * what the admin Settings page writes. Keep these in sync with that doc so there is no visible
 * flash on first paint. Before DRB-21 this object WAS the source of truth for the storefront
 * while the admin page wrote to Firestore, so every setting on that page was a silent no-op.
 *
 * DRB-28, Dr. Burns 2026-08-11: "Let's turn off free shipping for now" — the order-threshold
 * promo is OFF. The $100 she set on 2026-08-10 is retained so flipping it back needs one click,
 * not a re-decision.
 *
 * DRB-38, Dr. Burns 2026-08-11: "for now, please get rid of the SUBSCRIBE AND SAVE feature for
 * both products" — subscriptionsEnabled is OFF, which hides every subscription surface AND makes
 * both pricing paths treat every item as one-time. The DRB-28 note that used to sit here said the
 * perk was being kept deliberately; that was true when written and is superseded by her later
 * instruction, so it has been rewritten rather than left to mislead the next reader.
 * subscriptionFreeShipping and subscribeDiscount are retained but inert, so turning the feature
 * back on after DRB-35's recurring billing exists is one flag, not a re-decision.
 */
export const siteSettings: SiteSettings = {
  freeShippingEnabled: false, // OFF per Dr. Burns, 2026-08-11
  freeShippingThreshold: 10000, // $100 — retained, ignored while the flag is false
  subscriptionFreeShipping: true, // perk value, retained; irrelevant while subscriptionsEnabled is false
  subscriptionsEnabled: false, // DRB-38 — OFF per Dr. Burns, 2026-08-11: "get rid of ... for now"
  flatShippingRate: 695, // $6.95
  subscribeDiscount: 0.15,
  phoneOrderNumber: "",
  announcementBar:
    "Physician-formulated CBD, tested to a 99.5% accuracy rating.",
  squareEnabled: true, // DRB-18: card checkout is live. Real gate is the Square env vars.
  // DRB-4 — regulatory controls. Mirrored in functions/src/square/createPayment.ts
  // SETTINGS_FALLBACK; scripts/check-compliance-parity.mjs fails the build if they drift.
  ageGateEnabled: true,
  restrictedShippingStates: [...DEFAULT_RESTRICTED_STATES],
};
